import {
  Button,
  Chip,
  Group,
  MenuButton,
  Modal,
  Skeleton,
  Stack,
  Switch,
  Text,
  Title,
} from "@assetwatch/ui";
import { useState } from "react";
import {
  useDeleteProcedureMutation,
  useRestoreProcedureMutation,
} from "../../mutations/useProcedureLifecycleMutations";
import { useSetDefaultProcedureMutation } from "../../mutations/useSetDefaultProcedureMutation";
import type { ProcedureLibraryRecord } from "../../queries/procedureLibraryAdapter";
import {
  type ProcedureView,
  useProcedureDetailQuery,
} from "../../queries/useProceduresQuery";
import {
  countProcedurePreviewSteps,
  ProcedureDetailPreview,
} from "../snowflakes/procedures/ProcedureDetailPreview";
import { DeleteProcedureModal } from "./DeleteProcedureModal";
import { ProcedureLoadError } from "./ProcedureLoadError";

const STATUS_CHIPS: Record<
  string,
  { label: string; tone: "positive" | "informative" | "warning" }
> = {
  published: { label: "Published", tone: "positive" },
  draft: { label: "Draft", tone: "informative" },
  unpublished: { label: "Unpublished", tone: "warning" },
};

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
    new Date(value),
  );
}

function BackToLibraryButton({ onBack }: { onBack: () => void }) {
  return (
    <div className="border-b border-outline-subtle px-(--spacing-12) py-(--spacing-8) lg:hidden">
      <Button icon="arrow-left" onClick={onBack} size="sm" variant="ghost">
        Back to procedures
      </Button>
    </div>
  );
}

type ProcedureDetailPanelProps = {
  onBack: () => void;
  onDeleted: () => void;
  onEdit: () => void;
  procedure: ProcedureLibraryRecord | null;
  defaultProcedureId: string | null;
  defaultProcedureName: string | null;
  view: ProcedureView;
};

export function ProcedureDetailPanel({
  onBack,
  onDeleted,
  onEdit,
  procedure,
  defaultProcedureId,
  defaultProcedureName,
  view,
}: ProcedureDetailPanelProps) {
  const detailQuery = useProcedureDetailQuery(procedure?.id ?? "", view);
  const deleteMutation = useDeleteProcedureMutation();
  const restoreMutation = useRestoreProcedureMutation();
  const defaultMutation = useSetDefaultProcedureMutation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pendingDefaultProcedureId, setPendingDefaultProcedureId] = useState<
    string | null
  >(null);

  function handleDeleteOpenChange(open: boolean) {
    setDeleteOpen(open);
    if (!open) setDeleteError(null);
  }

  async function confirmDelete(procedureId: string, name: string) {
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync({ procedureId, name });
      setDeleteOpen(false);
      onDeleted();
    } catch (error) {
      // The mutation already raised the error toast; the modal stays open and
      // shows the reason so the deletion is never presented as done.
      setDeleteError(
        error instanceof Error
          ? error.message
          : "Unable to delete Procedure. Try again.",
      );
    }
  }

  async function confirmDefaultChange() {
    if (!pendingDefaultProcedureId) return;
    try {
      await defaultMutation.mutateAsync(pendingDefaultProcedureId);
      setPendingDefaultProcedureId(null);
    } catch {
      // The mutation shows the error toast; keep confirmation open to retry.
    }
  }

  function handleDefaultChange(checked: boolean) {
    if (!checked) {
      defaultMutation.mutate(null);
      return;
    }
    if (defaultProcedureId && defaultProcedureId !== procedure?.id) {
      setPendingDefaultProcedureId(procedure?.id ?? null);
      return;
    }
    if (procedure) defaultMutation.mutate(procedure.id);
  }

  if (!procedure) {
    return (
      <div className="grid min-h-80 place-items-center p-(--spacing-24) text-center">
        <Stack align="center" gap={8}>
          <Title as="h2" size="lg">
            Select a procedure
          </Title>
          <Text tone="subtle">
            Choose a procedure from the library to preview its published steps.
          </Text>
        </Stack>
      </div>
    );
  }
  if (detailQuery.isPending) {
    return (
      <>
        <BackToLibraryButton onBack={onBack} />
        <div className="p-(--spacing-20)">
          <Stack gap={16}>
            <Skeleton height={28} width="60%" />
            <Skeleton height={20} width="35%" />
            <Skeleton height={96} width="100%" />
            <Skeleton height={120} width="100%" />
          </Stack>
        </div>
      </>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <>
        <BackToLibraryButton onBack={onBack} />
        <ProcedureLoadError
          appearance="centered"
          description="We couldn’t load this published procedure. Retry to load it again."
          isRetrying={detailQuery.isFetching}
          onRetry={() => void detailQuery.refetch()}
          title="Preview unavailable"
        />
      </>
    );
  }
  const detail = detailQuery.data;
  const preview = detail.canonicalPreview;
  const categories =
    detail.category
      ?.split(",")
      .map((category) => category.trim())
      .filter(Boolean) ?? [];
  const stepCount =
    preview.status === "available" ? countProcedurePreviewSteps(preview) : null;
  const statusChip = STATUS_CHIPS[detail.status];
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <BackToLibraryButton onBack={onBack} />
      <div className="border-b border-outline-subtle p-(--spacing-20)">
        <Group align="start" justify="between" gap={16} wrap="wrap">
          <div className="min-w-0">
            {stepCount !== null ? (
              <Text size="sm" tone="subtle">
                {stepCount} {stepCount === 1 ? "step" : "steps"}
              </Text>
            ) : null}
            <Title as="h2" size="lg">
              {detail.name}
            </Title>
            {detail.deleted ? (
              <div className="mt-(--spacing-8)">
                <Chip label="Deleted" />
              </div>
            ) : statusChip ? (
              <div className="mt-(--spacing-8)">
                <Chip label={statusChip.label} tone={statusChip.tone} />
              </div>
            ) : null}
          </div>
          {/* Delete sits in the overflow menu beside Edit, as in the product
              mock. The mock's Copy item is intentionally not built. Restore is
              a button here pending product confirmation. */}
          <Group gap={8}>
            {detail.deleted && detail.canRestore ? (
              <Button
                loading={restoreMutation.isPending}
                loadingText="Restoring"
                onClick={() =>
                  restoreMutation.mutate({
                    procedureId: detail.id,
                    name: detail.name,
                  })
                }
                size="sm"
                variant="outline"
              >
                Restore procedure
              </Button>
            ) : null}
            {preview.status === "available" && detail.canEdit ? (
              <Button onClick={onEdit} size="sm">
                Edit procedure
              </Button>
            ) : null}
            {detail.canDelete ? (
              <MenuButton
                aria-label="More procedure actions"
                items={[
                  {
                    id: "delete",
                    label: "Delete",
                    leadingIcon: "trash-delete-bin",
                    onSelect: () => setDeleteOpen(true),
                    tone: "destructive",
                  },
                ]}
                size="sm"
                variant="overflow"
              />
            ) : null}
          </Group>
        </Group>
        {detail.description ? (
          <div className="mt-(--spacing-8)">
            <Text tone="subtle">{detail.description}</Text>
          </div>
        ) : null}
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto p-(--spacing-20)"
        data-testid="procedures-detail-scroll-region"
      >
        {detail.canSetDefault ? (
          <div className="border-b border-outline-subtle pb-(--spacing-16)">
            <Group align="center" justify="between" gap={16} wrap="wrap">
              <div>
                <Text weight="semibold">Default for new Work Orders</Text>
                <Text size="sm" tone="subtle">
                  Preselects this Procedure when creating a Work Order at this
                  site.
                </Text>
              </div>
              <Switch
                aria-label="Set as default Procedure"
                checked={defaultProcedureId === detail.id}
                disabled={defaultMutation.isPending}
                onCheckedChange={handleDefaultChange}
              />
            </Group>
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-(--spacing-16) border-b border-outline-subtle pb-(--spacing-16) sm:grid-cols-2">
          {categories.length > 0 ? (
            <div>
              <Text size="sm" tone="subtle">
                Category
              </Text>
              <div className="mt-(--spacing-4) flex flex-wrap gap-(--spacing-6)">
                {categories.map((category) => (
                  <span
                    className="rounded-(--radius-full) bg-primary-container px-(--spacing-8) py-(--spacing-4) text-(length:--font-size-label-small) text-primary-on-container"
                    key={category}
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <div>
            <Text size="sm" tone="subtle">
              Owner
            </Text>
            <Text>{procedure.owner ?? "Unavailable"}</Text>
          </div>
          <div>
            <Text size="sm" tone="subtle">
              Last updated
            </Text>
            <Text>{formatDate(detail.updatedAt)}</Text>
          </div>
        </div>
        <div className="pt-(--spacing-20)">
          <Text size="sm" tone="subtle" weight="semibold">
            Steps
          </Text>
          {preview.status === "unavailable" ? (
            <div className="rounded-(--radius-sm) border border-outline-subtle bg-surface-container-low p-(--spacing-16)">
              <Stack gap={8}>
                <Title as="h3" size="sm">
                  Canonical preview unavailable
                </Title>
                <Text tone="subtle">
                  The current API does not persist canonical hierarchy data. The
                  legacy instruction, tool, and caution fields are intentionally
                  not presented as the canonical preview.
                </Text>
              </Stack>
            </div>
          ) : (
            <div className="mt-(--spacing-12)">
              <ProcedureDetailPreview preview={preview} />
            </div>
          )}
        </div>
      </div>
      <DeleteProcedureModal
        error={deleteError}
        isPending={deleteMutation.isPending}
        onConfirm={() => void confirmDelete(detail.id, detail.name)}
        onOpenChange={handleDeleteOpenChange}
        open={deleteOpen}
        title={detail.name}
      />
      <Modal
        dismissible={!defaultMutation.isPending}
        footer={
          <>
            <Button
              disabled={defaultMutation.isPending}
              onClick={() => setPendingDefaultProcedureId(null)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              loading={defaultMutation.isPending}
              loadingText="Updating default"
              onClick={() => void confirmDefaultChange()}
            >
              Replace default
            </Button>
          </>
        }
        onOpenChange={(open) => {
          if (!open && !defaultMutation.isPending)
            setPendingDefaultProcedureId(null);
        }}
        open={pendingDefaultProcedureId !== null}
        title="Replace default Procedure?"
      >
        <Text>
          {`“${detail.name}” will replace “${defaultProcedureName ?? "the current default Procedure"}” for new Work Orders at this site.`}
        </Text>
      </Modal>
    </div>
  );
}
